import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShareButton } from '../components/ShareButton';
import { useModalA11y } from '../components/useModalA11y';
import { useScrimDismiss } from '../components/useScrimDismiss';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PauseIcon,
  PlayIcon,
  StarIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { AssetDetail, GalleryAsset, Rating, RotateDirection } from '@xphoto/shared';
import { useAuth, useCanWrite } from '../auth';
import { useI18n, type MessageKey } from '../i18n';
import { UndoBanner } from '../components/UndoBanner';
import { recordAction } from '../diagnostics/buffers';
import { interpolate } from '../gallery/format';
import { ApiError, thumbnailUrl } from '../gallery/api';
import { LIBRARY_ID } from '../gallery/config';
import { requestGalleryRefresh } from '../gallery/gallery-refresh';
import { folderLinkFor } from '../gallery/url-state';
import { enqueueRotate } from '../organize/rotate-api';
import { restoreAssets, softDeleteAssets } from '../trash/trash-api';
import { dirnameOf, formatDateTime, formatDuration } from './format';
import { InfoPanel } from './InfoPanel';
import { RatingControl } from './RatingControl';
import { CommentsEditor } from './CommentsEditor';
import { ViewerDisplayMenu, type DisplayToggle } from './ViewerDisplayMenu';
import { ZoomableImage } from './ZoomableImage';
import { originalUrl } from './viewer-api';
import { useAssetDetail } from './use-asset-detail';
import { useSlideshow, type SlideshowMedia } from './use-slideshow';
import { useSwipeNav } from './use-swipe-nav';
import { useViewTracker } from './use-view-tracker';
import { getViewerDisplayPref, patchViewerDisplay } from './viewer-display-pref';
import type { ColdAssetErrorKind } from './use-cold-asset';

/**
 * Visionneuse plein écran + diaporama (XPHOTO-21).
 *
 * NAVIGATION-AGNOSTIQUE : le média courant (`asset`) et la navigation préc./suiv.
 * sont FOURNIS. Deux modes de montage (routing « tout dans l'URL ») :
 *  - CONTEXTE GALERIE : `position` porte l'index + le total ; préc./suiv. relient
 *    aux voisins de la grille (compteur affiché).
 *  - ACCÈS À FROID (`/photo/:id` copié-collé) : `position = null` → l'image
 *    s'affiche seule, sans compteur ni préc./suiv. (dégradation propre).
 *
 * - Image affichée à partir de l'ORIGINAL, zoomable/panoramable (`ZoomableImage`) ;
 *   un GIF animé s'anime nativement (c'est aussi l'original). Le bouton télécharge
 *   l'original.
 * - Vidéos (`mp4`/`webm`/`mov`…) : lues en `<video>` sur l'original — **muet**,
 *   **autoplay**, **boucle**, avec **contrôles** (poster = vignette). Requiert le
 *   support des requêtes Range côté API (206). Fallback texte si illisible.
 * - Clavier : ←/→ (préc./suiv.), Échap (fermer), `i` (info), Espace (diaporama),
 *   `[`/`]` (pivoter à gauche/droite), `Suppr` (mettre à la poubelle) — les trois
 *   derniers réservés à l'accès ÉCRITURE.
 * - ACTIONS D'ÉCRITURE dans la barre du haut, à GAUCHE (cf. le commentaire du groupe) :
 *   pivoter à gauche/droite et mettre à la poubelle, pour trier une grande bibliothèque
 *   sans jamais quitter le plein écran.
 * - Modale accessible : `role="dialog"`, `aria-modal`, focus initial + restauré,
 *   défilement du corps verrouillé.
 */

/**
 * Message d'échec du chargement à froid, par NATURE (audit F1). Jamais un « Erreur »
 * indifférencié : « ce média n'existe plus » et « vous n'y avez pas accès » appellent
 * des réactions opposées de l'utilisateur.
 */
const COLD_ERROR_KEY: Record<ColdAssetErrorKind, MessageKey> = {
  notFound: 'viewer.errorNotFound',
  forbidden: 'viewer.errorForbidden',
  network: 'viewer.errorNetwork',
  unknown: 'viewer.error',
};

/** Conduite à tenir associée à chaque nature d'échec. */
const COLD_ERROR_HINT_KEY: Record<ColdAssetErrorKind, MessageKey> = {
  notFound: 'viewer.errorNotFoundHint',
  forbidden: 'viewer.errorForbiddenHint',
  network: 'viewer.errorNetworkHint',
  unknown: 'viewer.errorUnknownHint',
};

/**
 * Gabarit COMMUN des boutons-icônes de la barre du haut (forme, rembourrage, anneau de
 * focus) — les états (teinte active, survol) se composent par-dessus.
 *
 * `p-2` en mobile (cible de 40 px), `p-2.5` dès `sm` (44 px). Depuis l'arrivée des trois
 * actions d'écriture, la barre porte SEPT boutons sous `sm` : à 44 px partout elle
 * déborderait d'un écran de 375 px. 40 px reste très au-dessus du minimum WCAG 2.5.8
 * (24 px) et c'est déjà la taille des flèches préc./suiv. et de la barre du Tagueur.
 */
const BAR_BUTTON_CLASS =
  'rounded-md p-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:p-2.5';

/**
 * Bandeau éphémère du bas : soit la confirmation ANNULABLE d'une mise à la poubelle,
 * soit un message d'échec. Le `key` sérialise les notifications successives — remonter
 * le bandeau à chaque fois lui rend son plein délai d'affichage et le fait ré-annoncer
 * aux lecteurs d'écran, là où une simple mise à jour de texte passerait inaperçue.
 */
type ViewerNotice =
  | { key: number; kind: 'trashed'; assetId: string; filename: string }
  | { key: number; kind: 'error'; message: string };

/** Position dans une liste navigable (contexte galerie). `null` = accès à froid. */
export interface ViewerPosition {
  index: number;
  total: number;
}

interface ViewerProps {
  /** Média courant à afficher (`undefined` = en cours de chargement OU en erreur). */
  asset: GalleryAsset | undefined;
  /**
   * ÉCHEC du chargement du média à froid (`/photo/:id`), ou `null`/absent si le
   * chargement est en cours ou réussi. Sans cette information, `asset === undefined`
   * était interprété comme « en cours » et la visionneuse restait en « Chargement… »
   * PERPÉTUEL sur un id supprimé (404), un média hors périmètre (403) ou réseau coupé.
   */
  loadError?: ColdAssetErrorKind | null;
  /**
   * Détail DÉJÀ chargé pour `asset` par l'appelant (accès à froid / mode série /
   * diaporama, cf. `useColdAsset`). Sert de GRAINE à `useAssetDetail` → les panneaux
   * « Détail »/« Commentaires » s'ouvrent sans ré-émettre le `GET /api/assets/:id`
   * que l'appelant vient de faire (audit A1). Absent en contexte galerie à chaud
   * (l'asset vient de la grille, personne n'a chargé le détail).
   */
  detail?: AssetDetail | null;
  /** Position dans la liste (compteur + préc./suiv.), ou `null` en accès à froid. */
  position: ViewerPosition | null;
  /** Va au média précédent (no-op si indisponible). */
  onPrev: () => void;
  /** Va au média suivant (no-op si indisponible). */
  onNext: () => void;
  /** Préc. disponible (sinon flèche désactivée). */
  canPrev: boolean;
  /** Suiv. disponible (sinon flèche désactivée). */
  canNext: boolean;
  /** Ferme la visionneuse (revient à la route galerie précédente). */
  onClose: () => void;
  /** Répercute un changement de note vers la grille (tri « les mieux notées »). */
  onRatingChange?: (assetId: string, rating: Rating) => void;
  /**
   * Démarre le DIAPORAMA automatiquement au MONTAGE (drapeau de route « slideshow »,
   * posé par « Diaporama » sur une sélection). Une seule fois : mettre en pause reste
   * possible ensuite.
   */
  autoPlaySlideshow?: boolean;
}

export function Viewer({
  asset,
  loadError = null,
  detail: seedDetail = null,
  position,
  onPrev,
  onNext,
  canPrev,
  canNext,
  onClose,
  onRatingChange,
  autoPlaySlideshow = false,
}: ViewerProps): React.JSX.Element {
  const { t, locale } = useI18n();
  // Accès ÉCRITURE (superadmin/admin/propriétaire). Un LECTEUR (bénéficiaire de lien
  // d'accès) n'édite PAS les commentaires : le fil reste VISIBLE en consultation. La
  // NOTE (★) reste, elle, ouverte aux lecteurs (choix produit « collaboratif assumé »).
  const canWrite = useCanWrite();
  // Bibliothèque courante — MÊME dérivation que `GalleryView`/`ViewerRoute` (compte actif,
  // repli d'env). La visionneuse ne la recevait pas en prop : elle ne mutait rien avant
  // les actions d'écriture de la barre du haut.
  const { user } = useAuth();
  const libraryId = user?.libraryId ?? LIBRARY_ID;
  // Options d'affichage PERSISTÉES (localStorage) : lues UNE fois au montage, réécrites
  // à chaque bascule (cf. l'effet de synchro plus bas). « Détail » = panneau latéral
  // complet (ancien bouton Info) ; « Info »/captions = légende COURTE en surimpression.
  const [initialDisplay] = useState(getViewerDisplayPref);
  const [showDetail, setShowDetail] = useState(initialDisplay.showDetail);
  const [showCaptions, setShowCaptions] = useState(initialDisplay.showCaptions);
  // Barres d'édition en HAUT de l'image (note / commentaires), bascule indépendante.
  const [showRating, setShowRating] = useState(initialDisplay.showRating);
  const [showComments, setShowComments] = useState(initialDisplay.showComments);
  // SON des vidéos. Il n'y a PAS de bouton maison : le `<video>` porte déjà `controls`,
  // donc un bouton muet natif, sous la main de l'utilisateur. On se contente d'ÉCOUTER
  // ce qu'il en fait (`onVolumeChange`) et de le RETENIR — le `<video>` étant remonté à
  // chaque média (`key={asset.id}`), le son activé retombait sinon à muet à la
  // diapositive suivante, ce qui rendait le diaporama sonore inutilisable.
  const [soundOn, setSoundOn] = useState(initialDisplay.slideshowSound);

  // Persiste les bascules d'affichage + le son à chaque changement — y compris via le
  // raccourci « i » (showDetail). `patchViewerDisplay` fusionne pour ne pas écraser
  // boucle/vitesse/plafond du diaporama (écrits de leur côté par `use-slideshow`).
  useEffect(() => {
    patchViewerDisplay({
      showCaptions,
      showDetail,
      showRating,
      showComments,
      slideshowSound: soundOn,
    });
  }, [showCaptions, showDetail, showRating, showComments, soundOn]);
  const [imgFailed, setImgFailed] = useState(false);
  // Note affichée : optimiste local (vient de l'asset ; MAJ immédiate au clic étoile).
  const [rating, setRating] = useState<Rating>(asset?.rating ?? 0);
  const dialogRef = useRef<HTMLDivElement>(null);

  // --- ACTIONS D'ÉCRITURE (pivoter / mettre à la poubelle) ---------------------------

  /**
   * ROTATION OPTIMISTE : « Pivoter » enfile un JOB (l'original est réécrit en tâche de
   * fond, cf. `rotate.ts`). Sans retour immédiat, l'utilisateur reclique en croyant que
   * rien ne s'est passé — on fait donc tourner les pixels à l'écran tout de suite.
   * Cumulatif (deux clics = 180°), remis à zéro dès que le média change OU que sa
   * révision change (l'image fraîche porte DÉJÀ la rotation : la garder ferait tourner
   * deux fois).
   */
  const [rotationDeg, setRotationDeg] = useState(0);
  /** Rotation en vol (job non encore accepté) : verrouille les deux boutons (anti double-clic). */
  const [rotateBusy, setRotateBusy] = useState(false);
  /** Bandeau éphémère du bas (cf. `ViewerNotice`), et son compteur de séquence. */
  const [notice, setNotice] = useState<ViewerNotice | null>(null);
  const noticeSeq = useRef(0);
  const dismissNotice = useCallback(() => setNotice(null), []);

  // DÉTAIL COMPLET (EXIF brut, tags, commentaires) — chargé UNIQUEMENT quand un panneau
  // le consomme réellement (audit A2). Ses deux seuls consommateurs sont gatés :
  // `InfoPanel` derrière « Détail », `CommentsEditor` derrière « Commentaires ». Avant,
  // le hook était inconditionnel : feuilleter 300 photos tous panneaux fermés
  // téléchargeait 300 snapshots ExifTool pour n'en rien afficher. La légende de bas
  // d'écran, seul autre consommateur, lit désormais `asset.relPath` (déjà en main).
  const needDetail = showDetail || showComments;
  const detailState = useAssetDetail(needDetail ? (asset?.id ?? null) : null, seedDetail);

  // Compte une vue de la photo réellement regardée (debounce + dédoublonnage session).
  useViewTracker(asset?.id ?? null);

  // Réinitialise l'échec d'image + la note locale à chaque changement de média.
  useEffect(() => {
    setImgFailed(false);
    setRating(asset?.rating ?? 0);
  }, [asset?.id, asset?.rating]);

  // Annule la rotation OPTIMISTE dès que le média change (autre photo) ou que sa
  // RÉVISION change (`rev` = préfixe du `file_hash`, qui bouge à chaque écriture EXIF) :
  // dans les deux cas, les pixels affichés portent déjà la bonne orientation.
  useEffect(() => {
    setRotationDeg(0);
  }, [asset?.id, asset?.rev]);

  // Avance du diaporama : boucle si activé, sinon s'arrête en fin de liste. En accès
  // à froid (pas de liste), le diaporama est inerte (rien à enchaîner).
  const advance = useCallback(() => {
    if (canNext) {
      onNext();
    } else if (slideshowLoopRef.current && position) {
      goToStartRef.current();
    } else {
      setPlayingRef.current(false);
    }
  }, [canNext, onNext, position]);

  // Média courant du point de vue de la minuterie. Une VIDÉO signale sa propre fin
  // (`ended`) ; une image ANIMÉE porte la durée d'un cycle, mesurée au scan par sharp —
  // une image statique n'en a pas, `durationS` y est `null`. La POLITIQUE (plancher,
  // plafond, attente de `ended`) vit dans `use-slideshow`, pas ici.
  const slideshowMedia = useMemo<SlideshowMedia | null>(
    () => (asset ? { awaitsEnd: asset.kind === 'video', durationS: asset.durationS } : null),
    [asset],
  );

  const slideshow = useSlideshow(advance, position?.index ?? 0, slideshowMedia);
  // Refs pour que `advance` (stable) lise les valeurs courantes sans se recréer.
  const slideshowLoopRef = useRef(slideshow.loop);
  const setPlayingRef = useRef(slideshow.setPlaying);
  const goToStartRef = useRef<() => void>(() => {});
  useEffect(() => {
    slideshowLoopRef.current = slideshow.loop;
    setPlayingRef.current = slideshow.setPlaying;
  });

  // `muted` est posé IMPÉRATIVEMENT sur l'élément : React le rend en PROPRIÉTÉ, et
  // l'attribut ne suit pas toujours au remontage — un `<video autoPlay>` dont `muted`
  // n'est pas encore appliqué se fait refuser la lecture par la politique d'autoplay.
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = videoRef.current;
    if (el === null) return;
    el.muted = !soundOn;
    // La lecture peut être REFUSÉE quand le son est actif sans qu'aucun geste
    // utilisateur n'ait encore été enregistré (politique d'autoplay). On retombe alors
    // en muet plutôt que de laisser une diapositive figée — SANS toucher à la
    // préférence : le choix de l'utilisateur reste ce qu'il est, c'est le navigateur
    // qui refuse ici, et le média suivant retentera.
    void el.play().catch(() => {
      el.muted = true;
      void el.play().catch(() => {
        /* Lecture impossible (format, fichier absent) : `onError` prend le relais. */
      });
    });
  }, [soundOn, asset?.id]);

  // Démarrage AUTO du diaporama au MONTAGE quand demandé (drapeau de route « slideshow »,
  // posé par « Diaporama » sur une sélection). One-shot : `autoPlayRef` fige la valeur
  // initiale ; `setSlideshowPlaying` (setter `useState`, stable) laisse l'effet ne
  // s'exécuter qu'une fois → l'utilisateur peut ensuite mettre en pause.
  const setSlideshowPlaying = slideshow.setPlaying;
  const autoPlayRef = useRef(autoPlaySlideshow);
  useEffect(() => {
    if (autoPlayRef.current) setSlideshowPlaying(true);
  }, [setSlideshowPlaying]);

  // Verrou de défilement + `inert` sur l'arrière-plan + focus initial/restauré +
  // PIÈGE DE FOCUS (Tab cycle dans la visionneuse). Échap est traité par le
  // gestionnaire de raccourcis ci-dessous, qui porte déjà la logique complète.
  useModalA11y({ dialogRef, initialFocusRef: dialogRef });

  const counter = position
    ? interpolate(t('viewer.counter'), { index: position.index + 1, total: position.total }, locale)
    : null;
  const takenAt = asset ? formatDateTime(asset.takenAt, locale) : null;
  const speedLabel = interpolate(
    t('viewer.speedValue'),
    { seconds: slideshow.speedSeconds },
    locale,
  );
  // Plafond d'attente d'un média long (`0` = sans limite → libellé dédié, jamais « 0 s »).
  const capLabel =
    slideshow.capSeconds > 0
      ? interpolate(t('viewer.capValue'), { seconds: slideshow.capSeconds }, locale)
      : t('viewer.capUnlimited');

  const caption = useMemo(() => {
    if (!asset) return null;
    return takenAt ? `${asset.filename} · ${takenAt}` : asset.filename;
  }, [asset, takenAt]);

  /**
   * Durée du média, affichée dans la légende — `null` pour une image STATIQUE.
   *
   * `durationS` porte la durée du MÉDIA : celle d'ExifTool pour une vidéo, et celle d'un
   * CYCLE d'animation pour un GIF/WebP/APNG (mesurée au scan depuis les délais de frames,
   * v1.40.0). Tester la seule présence de la valeur suffit donc à couvrir les deux cas
   * sans regarder `kind` : une image statique n'a jamais de durée.
   *
   * ⚠ Une image animée indexée AVANT la v1.40.0 n'a pas encore sa durée : elle n'affiche
   * rien ici jusqu'au prochain scan. Absence d'information, pas information fausse.
   */
  const durationLabel = useMemo(() => {
    const seconds = asset?.durationS ?? null;
    if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
    return formatDuration(seconds);
  }, [asset]);

  // Répertoire (dossier) du fichier. Lu sur l'ASSET (`relPath` fait partie du DTO de
  // grille) et non plus sur le détail complet : c'était le seul consommateur permanent
  // de `useAssetDetail`, et il forçait le téléchargement de l'EXIF brut de chaque photo
  // feuilletée pour calculer un nom de dossier déjà disponible localement (audit A2).
  // Bonus : le chemin s'affiche désormais IMMÉDIATEMENT (plus d'attente du détail).
  // Vide (fichier à la racine) ⇒ rien.
  const captionDir = useMemo(() => {
    if (!asset) return null;
    const dir = dirnameOf(asset.relPath);
    return dir === '' ? null : dir;
  }, [asset]);

  // Note posée depuis la barre d'édition : MAJ locale + détail + grille (optimiste ;
  // la persistance/revert sont gérés dans `RatingControl`).
  const applyRating = useCallback(
    (next: Rating) => {
      setRating(next);
      if (asset) {
        if (detailState.detail && detailState.detail.id === asset.id) {
          detailState.setDetail({ ...detailState.detail, rating: next });
        }
        onRatingChange?.(asset.id, next);
      }
    },
    [asset, detailState, onRatingChange],
  );

  /**
   * Les rotations ne concernent QUE les images : l'API les ignore explicitement sur une
   * vidéo (comptée « skipped »). Les deux boutons sont donc DÉSACTIVÉS plutôt que
   * masqués sur une vidéo — la barre garde la même forme d'un média à l'autre, et un
   * bouton grisé explique pourquoi rien ne se passe là où une case vide n'expliquerait
   * rien. Masqués, en revanche, pour un LECTEUR : c'est un droit qu'il n'a pas du tout.
   */
  const canRotate = asset !== undefined && asset.kind === 'image';

  /**
   * Pivote d'un quart de tour. L'affichage tourne IMMÉDIATEMENT (`rotationDeg`) puis le
   * job est enfilé ; un échec REVIENT sur la rotation optimiste et affiche la cause —
   * laisser l'image tournée après un refus (bibliothèque en lecture seule) serait un
   * mensonge à l'écran.
   *
   * Un job d'un seul média fera sonner la cloche de notifications : compromis assumé de
   * cet incrément (aucune touche au système de jobs).
   */
  const rotate = useCallback(
    async (direction: RotateDirection) => {
      if (!asset || !canWrite || !canRotate || rotateBusy || libraryId === '') return;
      const delta = direction === 'cw' ? 90 : -90;
      setRotateBusy(true);
      setNotice(null);
      setRotationDeg((deg) => deg + delta);
      try {
        await enqueueRotate(libraryId, { assetIds: [asset.id], direction });
        recordAction('viewer.rotate');
      } catch (err) {
        setRotationDeg((deg) => deg - delta);
        setNotice({
          key: ++noticeSeq.current,
          kind: 'error',
          message:
            err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED'
              ? t('rotate.errorWriteDisabled')
              : t('rotate.error'),
        });
      } finally {
        setRotateBusy(false);
      }
    },
    [asset, canWrite, canRotate, rotateBusy, libraryId, t],
  );

  /**
   * Met la photo à la poubelle SANS confirmation : c'est un soft-delete RÉVERSIBLE, et
   * sur une bibliothèque de plusieurs milliers de photos une modale par geste coûterait
   * plus cher que l'erreur qu'elle prévient. Le filet vient après (bandeau « Annuler »),
   * doublé par la Corbeille.
   *
   * Le VOISIN est capturé AVANT la mutation : une fois l'asset supprimé, la grille se
   * réindexe et « suivant » ne désignerait plus la même photo — on en sauterait une.
   * Puis `requestGalleryRefresh()` (local ET diffusé) : contrairement à `GalleryView`,
   * qui recharge lui-même et se contente donc de `broadcastGalleryRefresh()`, la
   * visionneuse n'est PAS la galerie — celle-ci doit être prévenue pour perdre sa tuile.
   *
   * Le verrou est un `ref`, PAS un état : il doit être lu et posé dans le même tour
   * synchrone. La touche `Suppr` MAINTENUE enfoncée produit une rafale d'événements
   * avant tout rendu — sans ce verrou, la même photo partait dix fois à la corbeille et
   * l'avance de dix voisins d'un coup faisait défiler la bibliothèque à l'aveugle.
   */
  const trashBusyRef = useRef(false);
  const moveToTrash = useCallback(async () => {
    if (!asset || !canWrite || libraryId === '' || trashBusyRef.current) return;
    trashBusyRef.current = true;
    const goToNeighbour = canNext ? onNext : null;
    const { id: assetId, filename } = asset;
    setNotice(null);
    try {
      await softDeleteAssets(libraryId, [assetId]);
      recordAction('trash.softDelete');
      setNotice({ key: ++noticeSeq.current, kind: 'trashed', assetId, filename });
      if (goToNeighbour) goToNeighbour();
      else onClose();
      requestGalleryRefresh();
    } catch (err) {
      setNotice({
        key: ++noticeSeq.current,
        kind: 'error',
        message:
          err instanceof ApiError && err.status === 403
            ? t('trash.errorForbidden')
            : t('trash.error'),
      });
    } finally {
      trashBusyRef.current = false;
    }
  }, [asset, canWrite, libraryId, canNext, onNext, onClose, t]);

  /**
   * « Annuler » : ressort la photo de la corbeille et resynchronise les vues. On NE
   * NAVIGUE PAS en arrière — revenir tout seul sur la photo restaurée désorienterait
   * plus que ça n'aiderait, l'utilisateur est déjà passé à la suivante.
   */
  const undoTrash = useCallback(
    (assetId: string) => {
      if (libraryId === '') return;
      void (async () => {
        try {
          await restoreAssets(libraryId, [assetId]);
          requestGalleryRefresh();
        } catch (err) {
          setNotice({
            key: ++noticeSeq.current,
            kind: 'error',
            message:
              err instanceof ApiError && err.status === 403
                ? t('trash.errorForbidden')
                : t('trash.error'),
          });
        }
      })();
    },
    [libraryId, t],
  );

  // Raccourcis clavier globaux tant que la visionneuse est ouverte. Les touches de
  // navigation/action sont ignorées si un champ de saisie a le focus (ex. notes) —
  // sauf Échap, qui doit toujours pouvoir fermer. DÉCLARÉ APRÈS les actions d'écriture :
  // elles figurent dans ses dépendances, et une `const` référencée avant sa déclaration
  // lèverait (zone morte temporelle).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const ae = document.activeElement as HTMLElement | null;
      const inField =
        ae !== null &&
        (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable);
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (inField) return;
      // RÉPÉTITION AUTOMATIQUE ignorée par les trois actions d'ÉCRITURE. Le verrou de
      // `moveToTrash` empêche d'envoyer DEUX FOIS la même photo à la corbeille, pas de
      // MARCHER : la touche maintenue enfoncée en supprimerait une par aller-retour
      // réseau, et le bandeau « Annuler » ne rattraperait que la dernière. Un appui =
      // une photo. Les flèches, elles, gardent leur répétition (feuilleter vite est le
      // comportement attendu, et ne détruit rien).
      if (e.repeat && (e.key === 'Delete' || e.key === '[' || e.key === ']')) return;
      switch (e.key) {
        // ←/→ : navigation préc./suiv. En diaporama, chaque saut manuel change
        // l'index → `resetKey` de `useSlideshow` relance le minuteur (défilement
        // plus rapide que l'intervalle sans quitter la lecture). `preventDefault`
        // pour que la NAV PHOTO prime : un `<video>` focalisé ne doit pas AUSSI
        // seeker de ±5 s sur les flèches (conflit contrôles vidéo ↔ nav).
        case 'ArrowLeft':
          e.preventDefault();
          if (canPrev) onPrev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (canNext) onNext();
          break;
        case 'i':
        case 'I':
          setShowDetail((v) => !v);
          break;
        case ' ':
          e.preventDefault();
          slideshow.toggle();
          break;
        // Actions d'ÉCRITURE au clavier — c'est ce qui rend le tri d'une grande
        // bibliothèque tenable : une main sur les flèches, l'autre sur `[`/`]`/`Suppr`.
        // Les crochets encadrent le sens de rotation comme dans les éditeurs d'images.
        // Chaque appel est GARDÉ dans son propre gestionnaire (droits, type de média,
        // job en vol) : rien à redupliquer ici.
        case '[':
          void rotate('ccw');
          break;
        case ']':
          void rotate('cw');
          break;
        case 'Delete':
          void moveToTrash();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext, canPrev, canNext, slideshow, rotate, moveToTrash]);

  /**
   * Les quatre bascules d'affichage, telles que le menu de débordement mobile les
   * consomme. Libellés NEUTRES (le nom de ce qui s'affiche), et non les libellés
   * d'action des boutons : dans un menu, l'état est porté par la case cochée — « Masquer
   * le détail, coché » se contredirait.
   */
  const displayToggles = useMemo<DisplayToggle[]>(
    () => [
      {
        id: 'rating',
        label: t('viewer.optionRating'),
        checked: showRating,
        onToggle: () => setShowRating((v) => !v),
      },
      {
        id: 'comments',
        label: t('viewer.optionComments'),
        checked: showComments,
        onToggle: () => setShowComments((v) => !v),
      },
      {
        id: 'caption',
        label: t('viewer.optionCaption'),
        checked: showCaptions,
        onToggle: () => setShowCaptions((v) => !v),
      },
      {
        id: 'detail',
        label: t('viewer.optionDetail'),
        checked: showDetail,
        onToggle: () => setShowDetail((v) => !v),
      },
    ],
    [t, showRating, showComments, showCaptions, showDetail],
  );

  // Balayage tactile préc./suiv. pour la VIDÉO (jamais « zoomée » → toujours actif).
  // Les contrôles vidéo natifs restent maîtres de leurs propres gestes (barre de
  // lecture) : le swipe ne se déclenche que sur un mouvement franchement horizontal
  // au-delà du seuil, un tap/scrub ne navigue pas.
  const videoSwipe = useSwipeNav({ enabled: true, canPrev, canNext, onPrev, onNext });

  /**
   * Marge autour du média : un clic FRANC dessus revient à la galerie. La garde du geste
   * entier n'est pas un luxe ici — sans elle, glisser la barre de lecture d'une VIDÉO
   * (appui sur le `<video>`) puis relâcher dans la marge émettait un `click` sur cette
   * marge (l'ancêtre commun) et FERMAIT la visionneuse en pleine manipulation.
   */
  const mediaScrim = useScrimDismiss(onClose);

  /**
   * MÊME raccourci pour les grands vides de la BARRE SUPÉRIEURE (entre le compteur et le
   * groupe d'actions) : une bande pleine largeur qui n'était ni un contrôle ni un fond,
   * donc un clic « mort ». Instance DISTINCTE de `mediaScrim` : la garde du geste entier
   * tient un drapeau, deux surfaces ne peuvent pas le partager.
   */
  const topBarScrim = useScrimDismiss(onClose);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('info.title')}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-overlay/95 text-white outline-none"
    >
      {/* Titre de niveau 1 de l'écran (la visionneuse n'en portait aucun) — visuel
          déjà porté par la légende et le compteur, donc `sr-only`. */}
      <h1 className="sr-only">{t('viewer.title')}</h1>

      {/* Barre supérieure : compteur + actions. Son grand vide central est traité comme la
          marge du média — un clic FRANC dessus revient à la galerie. Le compteur et le
          groupe d'actions sont des DESCENDANTS : la garde `target === currentTarget` de
          `useScrimDismiss` les exclut d'office (cliquer le compteur ne ferme pas, et le
          texte reste sélectionnable). */}
      <div {...topBarScrim} className="flex items-center gap-2 p-2">
        {/* Le compteur reste un ENFANT DIRECT de la barre : l'envelopper avec le groupe
            d'écriture ci-dessous rendrait inerte tout le côté gauche, alors que seuls les
            boutons et leur marge de sécurité doivent l'être.
            `min-w-0 truncate` : c'est LUI qui cède si la barre manque de place (un
            « 12 345 / 13 500 » est large), jamais les boutons — leurs groupes sont
            `shrink-0`. Un compteur écourté reste lisible ; une icône écrasée, non. */}
        <span className="min-w-0 truncate px-2 text-sm tabular-nums" aria-live="polite">
          {counter}
        </span>

        {/*
          ACTIONS D'ÉCRITURE — à GAUCHE, juste après le compteur, et nulle part ailleurs :

           · « Mettre à la poubelle » doit être le plus LOIN possible de « Fermer ». Ce
             sont les deux gestes que l'on enchaîne le plus vite en triant, et les
             confondre coûte cher dans un sens (on jette) comme dans l'autre (on sort).
           · La barre se lit alors par moitiés : à gauche on AGIT sur le fichier, à droite
             on le REGARDE et on SORT.
           · Le grand vide central reste d'un seul tenant — depuis la v1.30.2 il ferme la
             visionneuse au clic, et le fendre en deux par un îlot de boutons en ferait
             deux zones à viser au lieu d'une.

          Masqué pour un LECTEUR : il n'a aucun de ces droits (les routes lui renvoient
          403), une affordance grisée ne ferait que lui promettre ce qu'il n'aura pas.
          Absent aussi tant que le média n'est pas chargé (accès à froid) : il n'y a
          alors rien sur quoi agir. `-m-2 p-2` : MÊME marge de sécurité inerte de 8 px
          que le groupe de droite.
        */}
        {canWrite && asset && (
          <div className="-m-2 flex shrink-0 items-center gap-1 p-2">
            {/* Filet de séparation d'avec le compteur — décor pur (`aria-hidden`), et
                DANS le groupe pour être couvert par sa marge inerte. */}
            <span className="h-5 w-px bg-white/20" aria-hidden="true" />
            <button
              type="button"
              onClick={() => void rotate('ccw')}
              disabled={!canRotate || rotateBusy}
              title={t('rotate.ccw')}
              aria-label={t('rotate.ccw')}
              className={`${BAR_BUTTON_CLASS} hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent`}
            >
              <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => void rotate('cw')}
              disabled={!canRotate || rotateBusy}
              title={t('rotate.cw')}
              aria-label={t('rotate.cw')}
              className={`${BAR_BUTTON_CLASS} hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent`}
            >
              <ArrowUturnRightIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            {/* Poubelle : JAMAIS rouge au repos — une alarme permanente au milieu d'une
                barre d'outils cesse d'alerter, et la couleur ne porte de toute façon
                jamais le sens (c'est l'intitulé accessible qui le fait). La teinte
                `danger` n'apparaît qu'au survol/focus, à l'instant où le geste s'engage. */}
            <button
              type="button"
              onClick={() => void moveToTrash()}
              title={t('viewer.trash')}
              aria-label={t('viewer.trash')}
              className={`${BAR_BUTTON_CLASS} hover:bg-danger/20 hover:text-danger focus-visible:bg-danger/20 focus-visible:text-danger`}
            >
              <TrashIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* `-m-2 p-2` : MARGE DE SÉCURITÉ inerte de 8 px autour des actions — mêmes pixels
            à l'écran (la marge négative annule le rembourrage), boîte de clic élargie.
            Viser une icône et la manquer de peu ne doit pas fermer la visionneuse.
            `ms-auto` (et non `justify-between` sur la barre) : le groupe de droite reste
            collé au bord même quand un troisième enfant — le groupe d'écriture — s'est
            invité à gauche. */}
        <div className="-m-2 ms-auto flex shrink-0 items-center gap-1 p-2">
          {/* BASCULES D'AFFICHAGE — visibles dès `sm` seulement. En deçà, la barre porte
              désormais sept boutons : ce sont elles qui cèdent la place (elles ne font
              qu'AFFICHER, on peut donc les atteindre en deux gestes) et se replient dans
              le menu « ⋯ » juste après. Elles restent des cibles directes en desktop. */}
          <div className="hidden items-center gap-1 sm:flex">
            {/* Note (★) — bascule la barre d'édition « note » en haut de l'image.
                Actif → pastille teintée (jamais la couleur seule : + aria-pressed). */}
            <button
              type="button"
              onClick={() => setShowRating((v) => !v)}
              aria-pressed={showRating}
              title={showRating ? t('viewer.closeRating') : t('viewer.openRating')}
              aria-label={showRating ? t('viewer.closeRating') : t('viewer.openRating')}
              className={`${BAR_BUTTON_CLASS} transition-colors motion-reduce:transition-none ${
                showRating ? 'bg-accent-on-dark/15 text-accent-on-dark' : 'hover:bg-white/10'
              }`}
            >
              <StarIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            {/* Commentaire — bascule la barre d'édition « commentaires » en haut. */}
            <button
              type="button"
              onClick={() => setShowComments((v) => !v)}
              aria-pressed={showComments}
              title={showComments ? t('viewer.closeComments') : t('viewer.openComments')}
              aria-label={showComments ? t('viewer.closeComments') : t('viewer.openComments')}
              className={`${BAR_BUTTON_CLASS} transition-colors motion-reduce:transition-none ${
                showComments ? 'bg-accent-on-dark/15 text-accent-on-dark' : 'hover:bg-white/10'
              }`}
            >
              <ChatBubbleBottomCenterTextIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            {/* Info — légende COURTE (filename · date) en surimpression (ancien « Aa »). */}
            <button
              type="button"
              onClick={() => setShowCaptions((v) => !v)}
              aria-pressed={showCaptions}
              title={showCaptions ? t('viewer.hideInfo') : t('viewer.showInfo')}
              aria-label={showCaptions ? t('viewer.hideInfo') : t('viewer.showInfo')}
              className={`${BAR_BUTTON_CLASS} transition-colors motion-reduce:transition-none ${
                showCaptions ? 'bg-accent-on-dark/15 text-accent-on-dark' : 'hover:bg-white/10'
              }`}
            >
              <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            {/* Détail — panneau latéral complet (ancien bouton Info). */}
            <button
              type="button"
              onClick={() => setShowDetail((v) => !v)}
              aria-pressed={showDetail}
              title={showDetail ? t('viewer.hideDetail') : t('viewer.showDetail')}
              aria-label={showDetail ? t('viewer.hideDetail') : t('viewer.showDetail')}
              className={`${BAR_BUTTON_CLASS} transition-colors motion-reduce:transition-none ${
                showDetail ? 'bg-accent-on-dark/15 text-accent-on-dark' : 'hover:bg-white/10'
              }`}
            >
              <InformationCircleIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          {/* … et leur repli mobile (le composant se masque lui-même dès `sm`). */}
          <ViewerDisplayMenu
            label={t('viewer.displayOptions')}
            closeLabel={t('a11y.closeMenu')}
            toggles={displayToggles}
          />
          {/* Partager — crée un LIEN PUBLIC de cette seule photo (une « sélection »
              de un, figée). Action de LECTURE : jamais gardée par `canWrite`. */}
          {asset && (
            <ShareButton
              label={t('share.photo')}
              tone="dark"
              target={{ kind: 'selection', assetIds: [asset.id] }}
              defaultTitle={asset.filename}
            />
          )}
          {asset && (
            <a
              href={originalUrl(asset.id, asset.rev)}
              download
              title={t('viewer.download')}
              aria-label={t('viewer.download')}
              className={`${BAR_BUTTON_CLASS} hover:bg-white/10`}
            >
              <ArrowDownTrayIcon className="h-6 w-6" aria-hidden="true" />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            title={t('viewer.close')}
            aria-label={t('viewer.close')}
            className={`${BAR_BUTTON_CLASS} hover:bg-white/10`}
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/*
       * Corps : média (+ flèches) et panneau « Détail » optionnel.
       *
       * EMPILÉ EN MOBILE (`flex-col`), CÔTE À CÔTE dès `md`. Avant, la direction était
       * `row` À TOUTE TAILLE et l'`aside` `w-full shrink-0` : sur un viewport de 360 px
       * il réclamait les 360 px et refusait de se rétracter → ouvrir « Détail » rendait
       * la PHOTO INVISIBLE sur téléphone. Même patron que le Tagueur (`TagWalk`).
       */}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <div className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center">
          {/* Flèche précédente. */}
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            title={t('viewer.previous')}
            aria-label={t('viewer.previous')}
            className="absolute start-2 z-10 rounded-full bg-black/40 p-2 hover:bg-black/60 disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <ChevronLeftIcon className="h-8 w-8" aria-hidden="true" />
          </button>

          {/* Média. Un clic FRANC sur la marge du fond — appui ET relâchement dessus,
              jamais sur l'image/la vidéo/les contrôles — revient à la galerie. Le fond
              de l'image zoomable est géré finement dans `ZoomableImage`. */}
          {/* Ce `div` n'est PAS un contrôle : c'est le FOND du média, et le clic sur la
              marge n'est qu'un RACCOURCI de confort. La même action est offerte au
              clavier par `Échap` et par le bouton « Fermer » focalisable de la barre —
              lui donner un `role`/`tabIndex` ajouterait une escale de tabulation
              redondante et un élément interactif fantôme dans l'arbre d'accessibilité. */}
          <div {...mediaScrim} className="flex h-full w-full items-center justify-center p-4">
            {loadError !== null ? (
              // ÉCHEC de chargement du média (audit F1) : message DISCRIMINÉ par nature
              // (introuvable / accès refusé / réseau), icône + texte (jamais la couleur
              // seule), annoncé aux lecteurs d'écran (`role="alert"`).
              <div className="flex flex-col items-center gap-2 text-white/70" role="alert">
                <ExclamationTriangleIcon className="h-10 w-10" aria-hidden="true" />
                <p>{t(COLD_ERROR_KEY[loadError])}</p>
                <p className="text-sm">{t(COLD_ERROR_HINT_KEY[loadError])}</p>
              </div>
            ) : asset === undefined ? (
              <p className="text-sm text-white/70" role="status">
                {t('viewer.loading')}
              </p>
            ) : imgFailed ? (
              <div className="flex flex-col items-center gap-2 text-white/70">
                <ExclamationTriangleIcon className="h-10 w-10" aria-hidden="true" />
                <p>{t('viewer.error')}</p>
              </div>
            ) : asset.kind === 'image' ? (
              // Keyé sur id ET révision : quand la rotation aboutit, le fichier a de
              // NOUVELLES dimensions natives (l'API les recale). Or `ZoomableImage` ne
              // lit ses `natural*` qu'au montage — sans remontage, il cadrerait l'image
              // fraîche avec les dimensions de l'ancienne.
              <ZoomableImage
                key={asset.rev !== undefined ? `${asset.id}:${asset.rev}` : asset.id}
                src={originalUrl(asset.id, asset.rev)}
                alt={asset.filename}
                naturalWidth={asset.width}
                naturalHeight={asset.height}
                rotationDeg={rotationDeg}
                onError={() => setImgFailed(true)}
                onBackdropClick={onClose}
                onPrev={onPrev}
                onNext={onNext}
                canPrev={canPrev}
                canNext={canNext}
              />
            ) : (
              // Vidéo : lue sur l'original en autoplay, contrôles natifs. Cadrage
              // « contain » comme les images. Le poster = vignette 1024 (affiche quelque
              // chose avant/pendant la mise en tampon). Les touches ←/→ restent captées
              // par la fenêtre (nav préc./suiv.) ; les contrôles vidéo gèrent leur propre
              // lecture au clic/focus — et c'est leur bouton MUET qui pilote le son.
              <video
                key={asset.id}
                ref={videoRef}
                src={originalUrl(asset.id, asset.rev)}
                poster={thumbnailUrl(asset.id, 1024, asset.rev)}
                aria-label={asset.filename}
                muted={!soundOn}
                autoPlay
                // Boucle HORS diaporama uniquement. En diaporama, c'est `ended` qui fait
                // avancer : une vidéo en boucle ne se termine jamais, et la coupure au
                // bout de la cadence était exactement ce qu'on veut supprimer.
                loop={!slideshow.playing}
                controls
                playsInline
                onEnded={() => {
                  if (slideshow.playing) advance();
                }}
                onVolumeChange={(e) => {
                  const muted = e.currentTarget.muted;
                  // On n'enregistre QUE si la valeur diverge de la préférence. L'effet
                  // plus haut pose `muted` lui-même, et l'événement qu'il déclenche porte
                  // donc exactement la préférence : ce simple test distingue un geste de
                  // l'utilisateur de l'écho de notre propre écriture, sans drapeau ni ref.
                  if (muted === soundOn) setSoundOn(!muted);
                }}
                onPointerDown={videoSwipe.onPointerDown}
                onPointerUp={videoSwipe.onPointerUp}
                onPointerCancel={videoSwipe.onPointerCancel}
                onError={() => setImgFailed(true)}
                className="max-h-full max-w-full object-contain"
              >
                {t('viewer.videoUnsupported')}
              </video>
            )}
          </div>

          {/* Flèche suivante. */}
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            title={t('viewer.next')}
            aria-label={t('viewer.next')}
            className="absolute end-2 z-10 rounded-full bg-black/40 p-2 hover:bg-black/60 disabled:opacity-30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <ChevronRightIcon className="h-8 w-8" aria-hidden="true" />
          </button>

          {/* Barre d'édition en HAUT de l'image (note / commentaires), symétrique de
              la légende du bas. Surimpression semi-opaque ; défile si le contenu
              dépasse (fil de commentaires long). Responsive 375px. */}
          {asset && (showRating || showComments) && (
            <div className="absolute inset-x-0 top-0 z-20 flex max-h-[60%] flex-col gap-3 overflow-y-auto bg-black/50 p-3 text-white sm:mx-auto sm:max-w-lg sm:rounded-b-lg">
              {showRating && (
                <RatingControl assetId={asset.id} rating={rating} onRatingChange={applyRating} />
              )}
              {showComments &&
                (detailState.status === 'ready' && detailState.detail ? (
                  <CommentsEditor
                    detail={detailState.detail}
                    onDetailChange={detailState.setDetail}
                    readOnly={!canWrite}
                  />
                ) : detailState.status === 'error' ? (
                  <div className="flex flex-col items-start gap-2">
                    <p className="text-sm text-danger">{t('viewer.error')}</p>
                    <button
                      type="button"
                      onClick={detailState.reload}
                      className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                    >
                      {t('viewer.retry')}
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-white/70" role="status">
                    {t('viewer.loading')}
                  </p>
                ))}
            </div>
          )}

          {/* Légende (optionnelle). Nom · date sur une ligne toujours visible ; le
              CHEMIN (dossier) n'apparaît qu'en desktop (≥ sm) et une fois le détail
              chargé — masqué en mobile pour éviter tout débordement à 375px.
              `w-fit` : la boîte ÉPOUSE le texte. Sans elle, `inset-x-0` + `max-w-[90%]`
              en faisait un bandeau de 90 % de large, invisible sur fond noir mais bien
              présent — il interceptait les clics tout autour de la légende (zone morte)
              et peignait un long voile sombre en travers d'une photo en portrait. */}
          {showCaptions && caption && (
            <div className="absolute inset-x-0 bottom-16 mx-auto flex w-fit max-w-[90%] min-w-0 flex-col items-center gap-0.5 rounded-md bg-black/50 px-3 py-1 text-center text-sm">
              {/* Nom du fichier + durée. Le nom TRONQUE (`min-w-0` + `truncate`) et la
                  durée ne rétrécit JAMAIS (`shrink-0`) : c'est ce qui la garde lisible à
                  375 px sans avoir à la masquer en mobile — elle ne coûte qu'une
                  quarantaine de pixels, c'est le nom de fichier qui est long. */}
              <p className="flex max-w-full min-w-0 items-baseline justify-center gap-2">
                <span className="min-w-0 truncate">{caption}</span>
                {durationLabel !== null && (
                  <span className="shrink-0 tabular-nums text-white/70" title={t('info.duration')}>
                    {/* Un « 0:12 » nu n'a aucun sens à l'oreille, collé à un nom de
                        fichier : le lecteur d'écran annonce d'abord de quoi il s'agit. */}
                    <span className="sr-only">{t('info.duration')} : </span>
                    {durationLabel}
                  </span>
                )}
              </p>
              {captionDir && (
                // Chemin du DOSSIER, cliquable → ouvre ce dossier en mode « Dossiers »
                // (même URL que le fil d'Ariane / la grille de dossiers). Un vrai
                // `<Link>` rend un `<a href>` : clic-milieu / nouvel onglet natifs.
                // `stopPropagation` : le conteneur média capte des clics (fermeture).
                <Link
                  to={folderLinkFor(captionDir)}
                  title={captionDir}
                  onClick={(e) => e.stopPropagation()}
                  className="hidden max-w-full min-w-0 truncate text-xs text-white/70 underline-offset-2 hover:text-white hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:block"
                >
                  {captionDir}
                </Link>
              )}
            </div>
          )}

          {/* BANDEAU DE RETOUR D'ACTION (mise à la poubelle annulable, ou échec).
              Posé AU-DESSUS de la légende (`bottom-28`) pour ne recouvrir ni elle
              (`bottom-16`) ni les contrôles du diaporama (`bottom-2`). Le rail est
              `pointer-events-none` — comme celui du diaporama, ses moitiés vides
              n'intercepteraient sinon aucun clic sur le fond ; seule la pastille, qui
              se rétablit en `pointer-events-auto`, reçoit les siens.
              Il vit DANS l'arbre de la visionneuse : la fermer l'emporte, et c'est
              assumé — la Corbeille reste le vrai filet de sécurité. */}
          {notice && (
            <div className="pointer-events-none absolute inset-x-0 bottom-28 z-30 flex justify-center px-4">
              <UndoBanner
                key={notice.key}
                message={
                  notice.kind === 'trashed'
                    ? interpolate(t('viewer.trashed'), { name: notice.filename })
                    : notice.message
                }
                {...(notice.kind === 'trashed'
                  ? {
                      undo: {
                        label: t('viewer.undo'),
                        onUndo: () => undoTrash(notice.assetId),
                      },
                    }
                  : {})}
                onDismiss={dismissNotice}
              />
            </div>
          )}

          {/* Contrôles diaporama. La RANGÉE traverse tout l'écran alors qu'elle ne sert
              qu'à centrer trois boutons : `pointer-events-none` la rend TRANSPARENTE aux
              clics. Sans ça, ses deux moitiés vides — invisibles — interceptaient tout
              clic sur le bas du fond (aucune fermeture) et recouvraient au passage les
              contrôles natifs d'une vidéo plein cadre. Le groupe de boutons, lui, reçoit
              les clics, avec la même marge de sécurité inerte de 8 px que la barre du haut. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-2 flex items-center justify-center">
            <div className="pointer-events-auto -m-2 flex items-center gap-2 p-2">
              <button
                type="button"
                onClick={slideshow.toggle}
                aria-pressed={slideshow.playing}
                title={slideshow.playing ? t('viewer.pause') : t('viewer.play')}
                aria-label={slideshow.playing ? t('viewer.pause') : t('viewer.play')}
                className="rounded-full bg-white/10 p-2 hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {slideshow.playing ? (
                  <PauseIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <PlayIcon className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
              <button
                type="button"
                onClick={slideshow.cycleSpeed}
                title={t('viewer.speed')}
                aria-label={`${t('viewer.speed')} — ${speedLabel}`}
                className="rounded-md bg-white/10 px-3 py-1.5 text-sm tabular-nums hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {speedLabel}
              </button>
              <button
                type="button"
                onClick={slideshow.toggleLoop}
                aria-pressed={slideshow.loop}
                title={slideshow.loop ? t('viewer.loopOff') : t('viewer.loopOn')}
                aria-label={slideshow.loop ? t('viewer.loopOff') : t('viewer.loopOn')}
                className={`rounded-md px-3 py-1.5 text-sm hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
                  slideshow.loop ? 'bg-white/25' : 'bg-white/10'
                }`}
              >
                ∞
              </button>
              {/* Plafond d'attente d'un média qui va jusqu'au bout (vidéo, image animée).
                  Sans limite = on attend la fin réelle. Bouton TEXTE : la valeur EST
                  l'information, elle ne peut pas se dire par une icône. */}
              <button
                type="button"
                onClick={slideshow.cycleCap}
                title={t('viewer.cap')}
                aria-label={`${t('viewer.cap')} — ${capLabel}`}
                className="rounded-md bg-white/10 px-3 py-1.5 text-sm tabular-nums hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {capLabel}
              </button>
            </div>
          </div>
        </div>

        {/* Panneau détail latéral (drawer). */}
        {showDetail && (
          // En mobile : hauteur BORNÉE (45 vh) sous l'image, bordure en HAUT ; dès `md`,
          // colonne latérale de largeur fixe, bordure sur le côté.
          <aside className="max-h-[45vh] w-full shrink-0 overflow-y-auto border-t border-white/10 bg-surface p-4 text-fg md:max-h-none md:w-96 md:max-w-sm md:border-s md:border-t-0">
            {detailState.status === 'ready' && detailState.detail ? (
              <InfoPanel detail={detailState.detail} />
            ) : detailState.status === 'error' ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-sm text-danger">{t('viewer.error')}</p>
                <button
                  type="button"
                  onClick={detailState.reload}
                  className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  {t('viewer.retry')}
                </button>
              </div>
            ) : (
              <p className="text-sm text-muted" role="status">
                {t('viewer.loading')}
              </p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
